import { useMemo } from 'react';
import { Select } from '@ardoq/select';
import { NumberInput } from '@ardoq/forms';
import { FlexBox } from '@ardoq/layout';
import {
  RelativeDatePeriod,
  RelativeDateUnit,
  RelativeDateValue as RelativeDateValueType,
} from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { ensureRelativeDateValue, isCalendarWeek } from './relativeDateUtils';

type RelativeDateValueProps = {
  value: RelativeDateValueType | null | undefined;
  onValueChange: (value: RelativeDateValueType | null) => void;
  isDisabled?: boolean;
  menuPortalTarget?: HTMLElement | null;
  i18nLocale?: I18nLocale;
};

const buildPeriodOptions = (i18nLocale: I18nLocale) => [
  {
    options: [
      {
        label: t(i18nLocale, 'queryBuilder:relativeDate.this', 'This'),
        value: RelativeDatePeriod.THIS,
      },
      {
        label: t(i18nLocale, 'queryBuilder:relativeDate.last', 'Last'),
        value: RelativeDatePeriod.LAST,
      },
      {
        label: t(i18nLocale, 'queryBuilder:relativeDate.next', 'Next'),
        value: RelativeDatePeriod.NEXT,
      },
    ],
  },
  {
    options: [
      {
        label: t(
          i18nLocale,
          'queryBuilder:relativeDate.inTheNext',
          'In the next'
        ),
        value: RelativeDatePeriod.IN_THE_NEXT,
      },
      {
        label: t(
          i18nLocale,
          'queryBuilder:relativeDate.inThePast',
          'In the past'
        ),
        value: RelativeDatePeriod.IN_THE_PAST,
      },
    ],
  },
];

const buildUnitOptions = (i18nLocale: I18nLocale) => [
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.week', 'week'),
    value: RelativeDateUnit.WEEK,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.month', 'month'),
    value: RelativeDateUnit.MONTH,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.quarter', 'quarter'),
    value: RelativeDateUnit.QUARTER,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.year', 'year'),
    value: RelativeDateUnit.YEAR,
  },
];

const buildUnitOptionsPlural = (i18nLocale: I18nLocale) => [
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.days', 'days'),
    value: RelativeDateUnit.DAY,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.weeks', 'weeks'),
    value: RelativeDateUnit.WEEK,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.months', 'months'),
    value: RelativeDateUnit.MONTH,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.quarters', 'quarters'),
    value: RelativeDateUnit.QUARTER,
  },
  {
    label: t(i18nLocale, 'queryBuilder:relativeDate.unit.years', 'years'),
    value: RelativeDateUnit.YEAR,
  },
];

const requiresAmount = (
  period: RelativeDatePeriod
): period is RelativeDatePeriod.IN_THE_NEXT | RelativeDatePeriod.IN_THE_PAST =>
  period === RelativeDatePeriod.IN_THE_NEXT ||
  period === RelativeDatePeriod.IN_THE_PAST;

const normalizeAmountOnBlur = (newValue: number): number => {
  // If user enters 0, any value less than 1, or a non-finite value (e.g. NaN), reset to 1
  if (!Number.isFinite(newValue) || newValue < 1) {
    return 1;
  }
  return newValue;
};

export const RelativeDateValue = ({
  value,
  onValueChange,
  isDisabled,
  menuPortalTarget,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: RelativeDateValueProps) => {
  const normalizedValue = ensureRelativeDateValue(value ?? null);
  const showAmountInput = requiresAmount(normalizedValue.period);

  const handlePeriodChange = (newPeriod: RelativeDatePeriod | null) => {
    if (!newPeriod) return;

    if (requiresAmount(newPeriod)) {
      // Switching to a period that requires an amount
      const amount = 'amount' in normalizedValue ? normalizedValue.amount : 1;
      onValueChange({
        period: newPeriod,
        amount,
        unit: normalizedValue.unit,
      });
    } else {
      // Switching to a simple period
      onValueChange({
        period: newPeriod,
        unit: normalizedValue.unit,
      });
    }
  };

  const handleAmountChange = (newAmount: number | null) => {
    if (!showAmountInput || !requiresAmount(normalizedValue.period)) return;

    onValueChange({
      period: normalizedValue.period,
      amount: newAmount ?? 1,
      unit: normalizedValue.unit,
    });
  };

  const handleUnitChange = (newUnit: RelativeDateUnit | null) => {
    if (!newUnit) return;

    if ('amount' in normalizedValue) {
      onValueChange({
        period: normalizedValue.period,
        amount: normalizedValue.amount,
        unit: newUnit,
      });
    } else {
      onValueChange({
        period: normalizedValue.period,
        unit: newUnit,
      });
    }
  };

  const periodOptions = useMemo(
    () => buildPeriodOptions(i18nLocale),
    [i18nLocale]
  );
  const unitOptions = useMemo(
    () =>
      showAmountInput
        ? buildUnitOptionsPlural(i18nLocale)
        : buildUnitOptions(i18nLocale),
    [i18nLocale, showAmountInput]
  );

  return (
    <FlexBox gap="xsmall" align="start" flex={1}>
      <Select
        aria-label={t(i18nLocale, 'queryBuilder:ariaLabel.period', 'Period')}
        placeholder={t(
          i18nLocale,
          'queryBuilder:placeholder.selectPeriod',
          'Select period...'
        )}
        value={normalizedValue.period}
        onValueChange={handlePeriodChange}
        options={periodOptions}
        isDisabled={isDisabled}
        dataTestId="relative-date-period"
        menuPortalTarget={menuPortalTarget}
      />
      {showAmountInput && 'amount' in normalizedValue && (
        <NumberInput
          aria-label={t(i18nLocale, 'queryBuilder:ariaLabel.amount', 'Amount')}
          value={normalizedValue.amount}
          onValueChange={handleAmountChange}
          onBlurTransformValue={normalizeAmountOnBlur}
          downArrowDisabled={normalizedValue.amount <= 1}
          isDisabled={isDisabled}
          data-testid="relative-date-amount"
        />
      )}
      <Select
        aria-label={t(i18nLocale, 'queryBuilder:ariaLabel.unit', 'Unit')}
        placeholder={t(
          i18nLocale,
          'queryBuilder:placeholder.selectUnit',
          'Select unit...'
        )}
        value={normalizedValue.unit}
        onValueChange={handleUnitChange}
        options={unitOptions}
        hintMessage={
          isCalendarWeek(normalizedValue)
            ? t(
                i18nLocale,
                'queryBuilder:relativeDate.weekStartHint',
                'Weeks run Monday to Sunday'
              )
            : undefined
        }
        isDisabled={isDisabled}
        dataTestId="relative-date-unit"
        menuPortalTarget={menuPortalTarget}
      />
    </FlexBox>
  );
};
